/**
 * Permission-based Access Control Constants
 *
 * Система разрешений для контроля доступа на фронтенде.
 * Должна быть синхронизирована с backend/app/core/permissions.py
 */

// Все доступные разрешения
export const Permission = {
  // Организации
  ORGANIZATIONS_READ: 'organizations:read',
  ORGANIZATIONS_READ_ALL: 'organizations:read_all',
  ORGANIZATIONS_WRITE: 'organizations:write',
  ORGANIZATIONS_DELETE: 'organizations:delete',
  ORGANIZATIONS_ASSIGN: 'organizations:assign',

  // Контакты
  CONTACTS_READ: 'contacts:read',
  CONTACTS_WRITE: 'contacts:write',
  CONTACTS_DELETE: 'contacts:delete',

  // Встречи
  MEETINGS_READ: 'meetings:read',
  MEETINGS_WRITE: 'meetings:write',
  MEETINGS_DELETE: 'meetings:delete',

  // Звонки
  CALLS_READ: 'calls:read',
  CALLS_WRITE: 'calls:write',
  CALLS_DELETE: 'calls:delete',

  // Сделки
  DEALS_READ: 'deals:read',
  DEALS_WRITE: 'deals:write',
  DEALS_DELETE: 'deals:delete',

  // Отчёты
  REPORTS_READ: 'reports:read',
  REPORTS_EXPORT: 'reports:export',

  // Настройки
  SETTINGS_READ: 'settings:read',
  SETTINGS_WRITE: 'settings:write',

  // Пользователи
  USERS_READ: 'users:read',
  USERS_WRITE: 'users:write',
  USERS_DELETE: 'users:delete',

  // API Токены
  API_TOKENS_READ: 'api_tokens:read',
  API_TOKENS_WRITE: 'api_tokens:write',
  API_TOKENS_DELETE: 'api_tokens:delete',

  // Педагог (будущие разделы)
  SCHEDULE_READ: 'schedule:read',
  SCHEDULE_WRITE: 'schedule:write',
  LESSONS_READ: 'lessons:read',
  LESSONS_WRITE: 'lessons:write',

  // Ученики
  STUDENTS_READ: 'students:read',
  STUDENTS_WRITE: 'students:write',
  STUDENTS_DELETE: 'students:delete',

  // Родители
  PARENTS_READ: 'parents:read',
  PARENTS_WRITE: 'parents:write',
  PARENTS_DELETE: 'parents:delete',

  // Личный кабинет
  CABINET_ACCESS: 'cabinet:access',
};

// Маппинг ролей на разрешения
export const ROLE_PERMISSIONS = {
  ADMIN: ['*'], // Wildcard - все разрешения

  MANAGER: [
    Permission.ORGANIZATIONS_READ_ALL,
    Permission.ORGANIZATIONS_WRITE,
    Permission.ORGANIZATIONS_DELETE,
    Permission.ORGANIZATIONS_ASSIGN,
    Permission.CONTACTS_READ,
    Permission.CONTACTS_WRITE,
    Permission.CONTACTS_DELETE,
    Permission.MEETINGS_READ,
    Permission.MEETINGS_WRITE,
    Permission.MEETINGS_DELETE,
    Permission.CALLS_READ,
    Permission.CALLS_WRITE,
    Permission.CALLS_DELETE,
    Permission.DEALS_READ,
    Permission.DEALS_WRITE,
    Permission.DEALS_DELETE,
    Permission.REPORTS_READ,
    Permission.REPORTS_EXPORT,
    Permission.STUDENTS_READ,
    Permission.STUDENTS_WRITE,
    Permission.STUDENTS_DELETE,
    Permission.PARENTS_READ,
    Permission.PARENTS_WRITE,
    Permission.PARENTS_DELETE,
  ],

  SALES: [
    Permission.ORGANIZATIONS_READ,
    Permission.ORGANIZATIONS_WRITE,
    Permission.CONTACTS_READ,
    Permission.CONTACTS_WRITE,
    Permission.MEETINGS_READ,
    Permission.MEETINGS_WRITE,
    Permission.CALLS_READ,
    Permission.CALLS_WRITE,
    Permission.DEALS_READ,
    Permission.DEALS_WRITE,
  ],

  TEACHER: [
    Permission.SCHEDULE_READ,
    Permission.SCHEDULE_WRITE,
    Permission.LESSONS_READ,
    Permission.LESSONS_WRITE,
  ],

  VIEWER: [
    Permission.ORGANIZATIONS_READ_ALL,
    // Viewer не имеет доступа к контактам
    Permission.MEETINGS_READ,
    Permission.CALLS_READ,
    Permission.DEALS_READ,
    Permission.REPORTS_READ,
  ],

  PARENT: [
    Permission.CABINET_ACCESS,
    Permission.STUDENTS_READ,
  ],
};

// Информация о ролях для UI
export const ROLE_INFO = {
  ADMIN: {
    code: 'ADMIN',
    name: 'Администратор',
    description: 'Полный доступ ко всем разделам системы',
    color: 'red',
  },
  MANAGER: {
    code: 'MANAGER',
    name: 'Менеджер',
    description: 'Все разделы кроме настроек',
    color: 'blue',
  },
  SALES: {
    code: 'SALES',
    name: 'Продавец',
    description: 'Организации (только свои) и связанные разделы',
    color: 'green',
  },
  TEACHER: {
    code: 'TEACHER',
    name: 'Педагог',
    description: 'Расписание и занятия (в разработке)',
    color: 'orange',
  },
  VIEWER: {
    code: 'VIEWER',
    name: 'Наблюдатель',
    description: 'Только просмотр всего контента',
    color: 'gray',
  },
  PARENT: {
    code: 'PARENT',
    name: 'Родитель',
    description: 'Личный кабинет родителя (дети, оплата, расписание)',
    color: 'cyan',
  },
};

/**
 * Проверить, имеет ли роль указанное разрешение
 * @param {string} role - Роль пользователя
 * @param {string} permission - Разрешение для проверки
 * @returns {boolean}
 */
export function hasPermission(role, permission) {
  const permissions = ROLE_PERMISSIONS[role] || [];

  // Wildcard - все разрешения
  if (permissions.includes('*')) {
    return true;
  }

  // Прямое совпадение
  if (permissions.includes(permission)) {
    return true;
  }

  // Проверка wildcard для ресурса (например "organizations:*")
  const resource = permission.split(':')[0];
  if (permissions.includes(`${resource}:*`)) {
    return true;
  }

  return false;
}

/**
 * Проверить, имеет ли роль хотя бы одно из указанных разрешений
 * @param {string} role - Роль пользователя
 * @param {string[]} permissions - Список разрешений
 * @returns {boolean}
 */
export function hasAnyPermission(role, permissions) {
  return permissions.some(p => hasPermission(role, p));
}
